import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import { SelectField } from "components/ui/select-field";

interface SalesforceIntegrationSetupProps {
  onIntegrationCreated: (integration: any) => void;
  onCancel: () => void;
}

export const SalesforceIntegrationSetup: React.FC<SalesforceIntegrationSetupProps> = ({
  onIntegrationCreated,
  onCancel
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [showClientSecret, setShowClientSecret] = useState(false);
  
  const [formData, setFormData] = useState({
    instanceUrl: '',
    clientId: '',
    clientSecret: '',
    username: '',
    password: '',
    securityToken: '',
    isSandbox: false,
    apiVersion: '58.0'
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateStep1 = () => {
    if (!formData.instanceUrl) {
      setError('Instance URL is required');
      return false;
    }
    if (!formData.clientId) {
      setError('Client ID is required');
      return false;
    }
    if (!formData.clientSecret) {
      setError('Client Secret is required');
      return false;
    }
    if (!formData.username) {
      setError('Username is required');
      return false;
    }
    if (!formData.password) {
      setError('Password is required');
      return false;
    }
    return true;
  };

  const testConnection = async () => {
    if (!validateStep1()) return;

    setLoading(true);
    setError('');
    setTestResult(null);

    try {
      // Mock connection test for now - replace with actual implementation later
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call
      
      const result = {
        success: true,
        message: 'Connection successful',
        orgInfo: {
          Name: formData.instanceUrl.replace('https://', '').replace('.salesforce.com', '') + ' Org',
          OrganizationType: formData.isSandbox ? 'Sandbox' : 'Production'
        }
      };
      
      setTestResult(result);
      
      if (result.success) {
        setStep(2);
      } else {
        setError(result.message || 'Connection test failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection test failed');
    } finally {
      setLoading(false);
    }
  };

  const createIntegration = async () => {
    setLoading(true);
    setError('');

    try {
      // Create a mock integration object that matches what IntegrationDashboard expects
      const mockIntegration = {
        getConfig: () => ({
          settings: {
            customSettings: {
              instanceUrl: formData.instanceUrl,
              isSandbox: formData.isSandbox,
              apiVersion: formData.apiVersion,
              clientId: formData.clientId,
              username: formData.username
            }
          }
        })
      };

      onIntegrationCreated(mockIntegration);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create integration');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-content mb-2">Salesforce Connection</h3>
        <p className="text-content-secondary">
          Connect your Salesforce org to sync contacts, leads, and activities with your email marketing platform.
        </p>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-300 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-300">Before you start</h4>
            <p className="text-blue-800 dark:text-blue-300 text-sm mt-1">
              You'll need to create a Connected App in Salesforce. Go to{' '}
              <strong>Setup → Apps → App Manager → New Connected App</strong> and configure:
            </p>
            <ul className="text-blue-800 dark:text-blue-300 text-sm mt-2 list-disc list-inside">
              <li>Enable OAuth Settings</li>
              <li>Add "Full access (full)" to OAuth Scopes</li>
              <li>Set Callback URL to your domain</li>
              <li>Enable "Require Secret for Web Server Flow"</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Instance URL *
          </label>
          <input
            type="url"
            value={formData.instanceUrl}
            onChange={(e) => handleInputChange('instanceUrl', e.target.value)}
            placeholder="https://yourorg.salesforce.com"
            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="text-xs text-content-muted mt-1">
            Your Salesforce org URL (e.g., https://yourorg.salesforce.com)
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            API Version
          </label>
          <SelectField
            value={formData.apiVersion}
            onChange={(e) => handleInputChange('apiVersion', e.target.value)}
            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="58.0">58.0 (Summer '23)</option>
            <option value="57.0">57.0 (Spring '23)</option>
            <option value="56.0">56.0 (Winter '23)</option>
            <option value="55.0">55.0 (Summer '22)</option>
          </SelectField>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Client ID *
          </label>
          <input
            type="text"
            value={formData.clientId}
            onChange={(e) => handleInputChange('clientId', e.target.value)}
            placeholder="Enter Connected App Client ID"
            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Client Secret *
          </label>
          <div className="relative">
            <input
              type={showClientSecret ? 'text' : 'password'}
              value={formData.clientSecret}
              onChange={(e) => handleInputChange('clientSecret', e.target.value)}
              placeholder="Enter Connected App Client Secret"
              className="w-full px-3 py-2 pr-10 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              type="button"
              onClick={() => setShowClientSecret(!showClientSecret)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-content-subtle hover:text-content-secondary"
            >
              {showClientSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Username *
          </label>
          <input
            type="email"
            value={formData.username}
            onChange={(e) => handleInputChange('username', e.target.value)}
            placeholder="your.email@company.com"
            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Password *
          </label>
          <input
            type="password"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            placeholder="Enter your Salesforce password"
            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Security Token
        </label>
        <input
          type="text"
          value={formData.securityToken}
          onChange={(e) => handleInputChange('securityToken', e.target.value)}
          placeholder="Enter security token (if required)"
          className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <p className="text-xs text-content-muted mt-1">
          Required if your IP is not in the trusted IP range. Get it from Setup → My Personal Information → Reset Security Token
        </p>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="isSandbox"
          checked={formData.isSandbox}
          onChange={(e) => handleInputChange('isSandbox', e.target.checked)}
          className="h-4 w-4 text-blue-600 dark:text-blue-300 focus:ring-blue-500 border-line-strong rounded"
        />
        <label htmlFor="isSandbox" className="ml-2 block text-sm text-content">
          This is a Sandbox environment
        </label>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-300" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-line-strong text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
        >
          Cancel
        </button>
        <button
          onClick={testConnection}
          disabled={loading}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Test Connection
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-content mb-2">Connection Successful!</h3>
        <p className="text-content-secondary">
          Your Salesforce integration is ready to be created.
        </p>
      </div>

      {testResult && testResult.success && (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-300" />
            <p className="text-green-800 dark:text-green-300 font-medium">Connected to Salesforce</p>
          </div>
          {testResult.orgInfo && (
            <div className="text-sm text-green-700 dark:text-green-300">
              <p><strong>Organization:</strong> {testResult.orgInfo.Name || 'Salesforce Org'}</p>
              {testResult.orgInfo.OrganizationType && (
                <p><strong>Type:</strong> {testResult.orgInfo.OrganizationType}</p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800/50 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-300 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-900 dark:text-yellow-300">Integration Features</h4>
            <p className="text-yellow-800 dark:text-yellow-300 text-sm mt-1">
              This integration will sync:
            </p>
            <ul className="text-yellow-800 dark:text-yellow-300 text-sm mt-2 list-disc list-inside">
              <li>Contacts and Leads bidirectionally</li>
              <li>Email engagement activities to Salesforce</li>
              <li>Campaign member status updates</li>
              <li>Custom field mappings</li>
            </ul>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-300" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={() => setStep(1)}
          className="px-4 py-2 border border-line-strong text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
        >
          Back
        </button>
        <button
          onClick={createIntegration}
          disabled={loading}
          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Create Integration
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-content">Salesforce Integration Setup</h2>
          <div className="flex items-center gap-2 text-sm text-content-secondary">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
              step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-dark-600 text-content-secondary'
            }`}>
              1
            </span>
            <span className="w-8 h-px bg-gray-300"></span>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
              step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-dark-600 text-content-secondary'
            }`}>
              2
            </span>
          </div>
        </div>
      </div>

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
    </div>
  );
};